import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "../components/dashboard/Sidebar/Sidebar.jsx";
import Topbar from "../components/dashboard/Topbar/Topbar.jsx";
import Toast from "../components/common/Toast/Toast.jsx";

// SIDEBAR WIDTH TOKENS — keep in sync with Sidebar.jsx's w-[240px]/w-[64px].
const SIDEBAR_EXPANDED = "md:ml-[240px]";
const SIDEBAR_COLLAPSED = "md:ml-[64px]";

const DashboardLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  // Tapping a menu link on a phone navigates but leaves this layout mounted,
  // so the drawer has to be closed here or it keeps covering the new page.
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    // No overflow-x-hidden here (deliberately) - per the CSS overflow spec,
    // setting overflow-x to anything but visible forces the OTHER axis to
    // compute as auto too, even if it's explicitly declared visible. That
    // makes this div a scroll container and becomes the nearest positioning
    // reference for every sticky descendant (Topbar, TicketPanel) - but
    // since this div only grows to fit its content instead of ever actually
    // scrolling (the real page scroll happens on <html>), sticky then never
    // engages. Wide content that used to rely on this for horizontal
    // clipping (e.g. tables) now gets its own overflow-x-auto wrapper
    // instead (TrucksTable.jsx, CreditsStep.jsx) - the mobile drawer below
    // is unaffected since it's `fixed`, already out of normal flow.
    <div className="app-shell flex min-h-screen bg-[var(--bg)]">
      {/* ── Mobile overlay backdrop ── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-[rgba(15,23,42,0.5)] z-[99] md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ── Desktop sidebar ── */}
      <div className="hidden md:block">
        <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} />
      </div>

      {/* ── Mobile drawer ── */}
      {/* w-[240px] is load-bearing: the Sidebar inside is position:fixed, so
          without it this wrapper is 0px wide and -translate-x-full moves it
          0px - the drawer then stays on screen and can't be closed. */}
      <div
        className={[
          "fixed top-0 left-0 bottom-0 w-[240px] z-[100] md:hidden",
          "transition-transform duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)]",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
        ].join(" ")}
      >
        <Sidebar collapsed={false} onToggle={() => setMobileOpen(false)} />
      </div>

      {/* ── Main content column ── */}
      <div
        className={[
          "flex-1 flex flex-col min-h-screen min-w-0",
          "transition-[margin-left] duration-[240ms] ease-[cubic-bezier(0.4,0,0.2,1)]",
          "ml-0",
          collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED,
        ].join(" ")}
      >
        <Topbar onMobileMenuToggle={() => setMobileOpen((v) => !v)} />
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>

      <Toast />
    </div>
  );
};

export default DashboardLayout;
